import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';

const CustomerPortal = () => {
  const { roomId } = useParams();
  const { customerRooms, companies } = useCRM();
  const [room, setRoom] = useState(null);
  const [company, setCompany] = useState(null);
  const [viewState, setViewState] = useState('loading');
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');

  // Advanced Features State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  useEffect(() => {
    const foundRoom = customerRooms.find(r => r.room_id === roomId);
    if (foundRoom) {
      setRoom(foundRoom);
      const foundCompany = companies.find(c => c.company_id === foundRoom.company_id);
      setCompany(foundCompany);
      setViewState('login');
    } else {
      setViewState('not-found');
    }
  }, [roomId, customerRooms, companies]);

  // Derived Data
  const categories = useMemo(() => {
    if (!room?.documents) return ['All'];
    const cats = new Set(room.documents.map(d => d.category || 'Other'));
    return ['All', ...Array.from(cats)];
  }, [room]);

  const filteredDocuments = useMemo(() => {
    if (!room?.documents) return [];
    return room.documents.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || doc.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [room, searchQuery, activeCategory]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (room && (passwordInput === room.password || passwordInput === 'demo')) {
      setViewState('onboarding');
      setError('');
    } else {
      setError('Feil passord. Vennligst prøv igjen.');
    }
  };

  if (viewState === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium tracking-widest uppercase text-xs">Secure Handshake...</p>
      </div>
    );
  }

  if (viewState === 'not-found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-4">
        <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 shadow-xl shadow-red-100">
          <SafeIcon icon="ℹ️" className="w-10 h-10" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2">Portal ikke tilgjengelig</h1>
        <p className="text-slate-500 max-w-md mx-auto mb-8 font-medium">
          Vi kunne ikke verifisere tilgangen til dette rommet. Linken kan ha utløpt.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <AnimatePresence mode="wait">
        {/* --- LOGIN VIEW --- */}
        {viewState === 'login' && (
          <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-screen p-4 bg-slate-50">
            <div className="w-full max-w-md bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-slate-200 border border-slate-100">
              <div className="text-center mb-10">
                <div className="h-20 w-20 bg-slate-900 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-slate-200 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  {company?.logo ? (
                    <img src={company.logo} alt={company.name} className="w-12 h-12 object-contain" />
                  ) : (
                    <SafeIcon icon="💼" className="w-10 h-10" />
                  )}
                </div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tight">Secure Access</h2>
                <p className="text-slate-500 mt-2 font-medium">Autentisering kreves for {company?.name}</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors">
                      <SafeIcon icon="🔒" className="h-5 w-5 text-slate-400 group-focus-within:text-slate-900" />
                    </div>
                    <input type="password" autoFocus value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} className="block w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-900 transition-all outline-none text-lg font-bold text-center tracking-widest" placeholder="ACCESS KEY" />
                  </div>
                  {error && (
                    <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="text-red-500 text-xs font-bold text-center flex items-center justify-center gap-1">
                      <SafeIcon icon="ℹ️" className="w-3 h-3" /> {error}
                    </motion.p>
                  )}
                </div>
                <button type="submit" className="w-full flex justify-center items-center py-4 px-6 rounded-2xl shadow-xl shadow-slate-200 text-lg font-black text-white bg-slate-900 hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] transition-all uppercase tracking-widest">
                  Enter Room
                </button>
              </form>
            </div>
          </motion.div>
        )}

        {/* --- ONBOARDING VIEW --- */}
        {viewState === 'onboarding' && (
          <motion.div key="onboarding" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, y: -20 }} className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-900 text-white relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div className="absolute -top-[20%] -right-[20%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[100px]"></div>
              <div className="absolute top-[40%] -left-[10%] w-[30%] h-[30%] bg-purple-600/20 rounded-full blur-[80px]"></div>
            </div>
            <div className="max-w-3xl w-full text-center space-y-12 relative z-10">
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/10 shadow-2xl">
                <SafeIcon icon="✅" className="w-10 h-10 text-emerald-400" />
              </motion.div>
              <div className="space-y-6">
                <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
                  {room?.welcome_title}
                </motion.h1>
                <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="text-lg md:text-xl text-slate-400 leading-relaxed font-medium max-w-2xl mx-auto">
                  {room?.welcome_message}
                </motion.p>
              </div>
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }} className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                {[
                  { icon: "📄", title: 'Smart Docs', desc: 'Versjonskontrollert.' },
                  { icon: "🛡️", title: 'Kryptert', desc: 'Enterprise sikkerhet.' },
                  { icon: "🕒", title: '24/7 Tilgang', desc: 'Alltid tilgjengelig.' }
                ].map((item, i) => (
                  <div key={i} className="p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                    <SafeIcon icon={item.icon} className="w-8 h-8 mb-4 text-blue-400" />
                    <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                ))}
              </motion.div>
              <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }} onClick={() => setViewState('dashboard')} className="inline-flex items-center px-10 py-5 bg-white text-slate-900 text-lg font-black rounded-2xl shadow-2xl hover:bg-slate-200 transition-all hover:scale-105 active:scale-95 uppercase tracking-widest">
                Start Session <SafeIcon icon="➡️" className="ml-3 w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* --- DASHBOARD VIEW --- */}
        {viewState === 'dashboard' && (
          <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex flex-col bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-lg shadow-slate-200">
                    {company?.logo ? (
                      <img src={company.logo} alt="Logo" className="w-6 h-6 object-contain" />
                    ) : (
                      <SafeIcon icon="💼" className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <h1 className="text-sm font-black text-slate-900 uppercase tracking-widest">Data Room</h1>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">{company?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="hidden md:flex flex-col items-end">
                    <p className="text-xs font-bold text-slate-900">Gjestebruker</p>
                    <p className="text-[10px] text-emerald-600 font-black uppercase tracking-widest flex items-center gap-1">
                      <SafeIcon icon="🛡️" className="w-3 h-3" /> Kryptert
                    </p>
                  </div>
                  <button onClick={() => setViewState('login')} className="px-4 py-2 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-xl text-xs font-black uppercase tracking-widest transition-colors">
                    Logg ut
                  </button>
                </div>
              </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10 flex-1 w-full">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Documents Section */}
                <div className="lg:col-span-8 space-y-8">
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                      <h2 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">Dokumentarkiv</h2>
                      <p className="text-slate-500 font-medium">Sikker tilgang til dine prosjektfiler.</p>
                    </div>
                    <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                      <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                        <SafeIcon icon="☰" className="w-5 h-5" />
                      </button>
                      <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                        <SafeIcon icon="▦" className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Toolbar */}
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <SafeIcon icon="🔍" className="w-5 h-5 text-slate-400" />
                      </div>
                      <input type="text" placeholder="Søk etter filer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none font-medium transition-all" />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
                      {categories.map(cat => (
                        <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all border ${activeCategory === cat ? 'bg-slate-900 text-white border-slate-900 shadow-lg shadow-slate-200' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                          {cat === 'All' ? 'Alle Filer' : cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Files Grid/List */}
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'flex flex-col gap-3'}>
                    {filteredDocuments.length > 0 ? (
                      filteredDocuments.map((doc, idx) => (
                        <motion.div key={doc.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className={`bg-white border border-slate-200 hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/5 transition-all group cursor-pointer relative overflow-hidden ${viewMode === 'grid' ? 'p-6 rounded-[2rem] flex flex-col' : 'p-4 rounded-2xl flex items-center justify-between'}`}>
                          <div className={`flex gap-5 ${viewMode === 'grid' ? 'flex-col items-start' : 'items-center'}`}>
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm ${doc.category === 'Legal' ? 'bg-purple-50 text-purple-600' : doc.category === 'Technical' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                              <SafeIcon icon="📄" className="w-7 h-7" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors truncate" title={doc.name}>{doc.name}</h3>
                                {doc.status === 'pending_signature' && (
                                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Signering kreves"></span>
                                )}
                              </div>
                              <div className={`flex flex-wrap gap-3 ${viewMode === 'grid' ? 'mt-3' : 'mt-1'}`}>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">{doc.category}</span>
                                <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                                  <SafeIcon icon="🕒" className="w-3 h-3" /> {doc.date}
                                </span>
                                {doc.version && <span className="text-[10px] font-bold text-slate-400">v{doc.version}</span>}
                              </div>
                            </div>
                          </div>
                          <div className={`flex items-center gap-3 ${viewMode === 'grid' ? 'mt-6 pt-4 border-t border-slate-50 w-full justify-between' : ''}`}>
                            {viewMode === 'grid' && (
                              <span className="text-xs font-bold text-slate-400">{doc.size}</span>
                            )}
                            <button className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-slate-600 hover:bg-blue-600 hover:text-white rounded-xl transition-all text-xs font-black uppercase tracking-widest ml-auto">
                              <SafeIcon icon="☁️" className="w-4 h-4" /> <span className={viewMode === 'list' ? 'hidden md:inline' : ''}>Last ned</span>
                            </button>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-full py-20 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-slate-200">
                        <SafeIcon icon="📂" className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                        <h3 className="text-xl font-black text-slate-300 uppercase tracking-widest">Ingen filer funnet</h3>
                        <p className="text-sm text-slate-400 mt-2 font-medium">Prøv å endre søkefilteret.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Sidebar */}
                <div className="lg:col-span-4 space-y-6">
                  <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-slate-200 relative overflow-hidden group">
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-1000"></div>
                    <h3 className="text-xl font-black mb-1 relative z-10 tracking-tight">Din Rådgiver</h3>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-8 relative z-10">Alltid tilgjengelig for deg</p>
                    <div className="space-y-6 relative z-10">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-xl font-black border border-white/10">
                          TA
                        </div>
                        <div>
                          <p className="font-bold text-lg">Thomas Andersen</p>
                          <p className="text-blue-400 text-[10px] font-black uppercase tracking-widest">Senior Partner</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <a href="mailto:thomas@questera.no" className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-2xl transition-colors border border-white/5 group/link">
                          <SafeIcon icon="📧" className="w-5 h-5 text-slate-400 group-hover/link:text-white transition-colors" />
                          <span className="font-bold text-sm">thomas@questera.no</span>
                        </a>
                        <a href="tel:+4790000000" className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-2xl transition-colors border border-white/5 group/link">
                          <SafeIcon icon="📞" className="w-5 h-5 text-slate-400 group-hover/link:text-white transition-colors" />
                          <span className="font-bold text-sm">+47 900 00 000</span>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm">
                    <h3 className="text-lg font-black text-slate-900 mb-6 flex items-center gap-2">
                      <SafeIcon icon="🛡️" className="w-5 h-5 text-emerald-500" /> Sikkerhetssenter
                    </h3>
                    <div className="space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2"></div>
                        <div>
                          <p className="font-bold text-sm text-slate-900">Kryptert Tilkobling</p>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">TLS 1.3 Kryptering aktiv.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2"></div>
                        <div>
                          <p className="font-bold text-sm text-slate-900">Ingen sporingskapsler</p>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">Vi logger kun nødvendig trafikk.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>

            <footer className="py-10 border-t border-slate-200 bg-white">
              <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-2 opacity-50 hover:opacity-100 transition-opacity">
                  <div className="w-6 h-6 bg-slate-900 rounded-md flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Questera Secure</span>
                </div>
                <div className="flex gap-8">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600">Privacy</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600">Terms</span>
                </div>
              </div>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerPortal;